import { useEffect, useRef } from 'react';
import { track } from '@/shared/analytics';

/**
 * Emits `performance_opened` once per project and tab.
 *
 * The guard is not defensive noise: React StrictMode mounts every component twice in
 * development, which would double every open in Amplitude. `AnalyticsProvider` guards the
 * same way for the same reason.
 *
 * It remembers which project and tab it last emitted, rather than merely that it has
 * emitted. `Performance` is the element of a pathless parent route and manage-campaigns is
 * one of its children, so moving between them changes `tab` in place with no remount — a
 * plain boolean meant the campaigns tab was only ever counted on a cold load of a
 * bookmarked URL, never on the in-app path, which is the comparison the event exists for.
 */
export function usePerformanceOpened(projectId: string, tab: 'dashboard' | 'campaigns'): void {
  const emitted = useRef<string | null>(null);

  useEffect(() => {
    const key = `${projectId}:${tab}`;
    if (emitted.current === key) return;
    emitted.current = key;
    track('performance_opened', { project_id: projectId, tab });
  }, [projectId, tab]);
}
